import React, { useState, useEffect } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import { FaPlus, FaSave, FaTrash, FaEraser, FaFileInvoiceDollar } from 'react-icons/fa';
import api from '../../services/api';
import { useHistory } from 'react-router-dom';
import { bin2hex, buscarCep, extract, CATEGORIA_FARMACEUTICO, CATEGORIA_NAO_FARMACEUTICO } from '../../util/util';
import ReactSelect from '../../components/ReactSelect';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { FinField, FinInput, FinTextarea } from '../../components/FinField';
import '../../css/financeiro-parcela-pagar.css';

const SITUACAO_OPTS = [
    { value: 'ativo', label: 'Ativo' },
    { value: 'inativo', label: 'Inativo' },
];

const TIPO_CADASTRO_OPTS = [
    { value: 'cliente', label: 'Cliente' },
    { value: 'fornecedor', label: 'Fornecedor' },
    { value: 'cliente,fornecedor', label: 'Cliente e Fornecedor' },
];

const MODELO_OPTS = [
    { value: 'master', label: 'Master' },
    { value: 'sanofi', label: 'Sanofi' },
    { value: 'chiesi', label: 'Chiesi' },
    { value: 'organon', label: 'Organon' },
    { value: 'abbvie', label: 'Abbvie' },
    { value: 'allergan', label: 'Allergan' },
    // D-0119: imposto 19,05% nas linhas da Access e no FEE; base do FEE = total da linha (longPingPolicy).
    { value: 'longping', label: 'Long Ping' },
];

// categoria do cliente: decide se os eventos dele usam os campos do ramo farmacêutico
// (CRM, conselho, especialidade) ou Cargo + Empresa
const CATEGORIA_CLIENTE_OPTS = [
    { value: CATEGORIA_FARMACEUTICO, label: 'Farmacêutico' },
    { value: CATEGORIA_NAO_FARMACEUTICO, label: 'Não Farmacêutico' },
];

const INDICADOR_INSCRICAO_OPTS = [
    { value: '1', label: 'Contribuinte' },
    { value: '2', label: 'Isento' },
    { value: '9', label: 'Não contribuinte' },
];

const REGIME_TRIBUTARIO_OPTS = [
    { value: '1', label: 'MEI' },
    { value: '2', label: 'Simples Nacional' },
    { value: '3', label: 'Simples Nacional (Sublimite)' },
    { value: '4', label: 'Lucro Presumido' },
];

export default function CadastroCliente({ match }) {
    const id = match.params.id;
    const token = localStorage.getItem('token');
    const [dados, setDados] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!id);
    const [feeRegrasAgencia, setFeeRegrasAgencia] = useState([]);
    // Contas da ACCESS (financeirocaixa) — opções do "onde este cliente deposita".
    // Lista curta e fixa, então carrega uma vez e vira `options` do ReactSelect:
    // com `loadOptions` o rótulo da conta já gravada só apareceria depois de o
    // operador abrir o menu, e o campo pareceria vazio num cliente configurado.
    const [contasAccess, setContasAccess] = useState([]);
    const [tab, setTab] = useState('geral');
    const history = useHistory();

    const regraVazia = () => ({
        faixaMinTotal: '',
        faixaMaxTotal: '',
        percentual: '',
        momento: 'todos'
    });

    const normalizarDecimal = (valor) => {
        if (valor === null || valor === undefined) return null;
        if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;

        const texto = String(valor).trim();
        if (!texto) return null;

        if (texto.includes(',') && texto.includes('.')) {
            const semMilhar = texto.replace(/\./g, '');
            const comPontoDecimal = semMilhar.replace(',', '.');
            const numero = Number(comPontoDecimal);
            return Number.isFinite(numero) ? numero : null;
        }

        const numero = Number(texto.replace(',', '.'));
        return Number.isFinite(numero) ? numero : null;
    };

    const formatarParaInput = (valor, casas = 2) => {
        const numero = normalizarDecimal(valor);
        if (numero === null) return '';
        return numero.toFixed(casas);
    };

    const carregarFeeRegrasAgencia = async (clienteId) => {
        if (!clienteId) {
            setFeeRegrasAgencia([]);
            return;
        }

        try {
            const response = await api({
                method: 'get',
                action: `/participante/${clienteId}/feeRegrasAgencia`,
                token: token,
            });

            const regras = Array.isArray(response?.data?.data) ? response.data.data : [];
            setFeeRegrasAgencia(regras.map((regra) => ({
                faixaMinTotal: formatarParaInput(regra.faixaMinTotal, 2),
                faixaMaxTotal: regra.faixaMaxTotal === null || regra.faixaMaxTotal === undefined ? '' : formatarParaInput(regra.faixaMaxTotal, 2),
                percentual: formatarParaInput(regra.percentual, 4),
                momento: regra.momento || 'todos',
            })));
        } catch (error) {
            console.error('Erro ao carregar regras de fee da agência:', error);
            setFeeRegrasAgencia([]);
        }
    };

    const validarENormalizarFeeRegrasParaSalvar = () => {
        const regrasPreenchidas = feeRegrasAgencia
            .map((regra) => ({
                faixaMinTotal: normalizarDecimal(regra.faixaMinTotal),
                faixaMaxTotal: normalizarDecimal(regra.faixaMaxTotal),
                percentual: normalizarDecimal(regra.percentual),
                momento: regra.momento || 'todos'
            }))
            .filter((regra) => regra.faixaMinTotal !== null || regra.faixaMaxTotal !== null || regra.percentual !== null);

        for (const regra of regrasPreenchidas) {
            const faixaMin = regra.faixaMinTotal ?? 0;
            const faixaMax = regra.faixaMaxTotal;

            if ((regra.percentual ?? 0) <= 0) {
                throw new Error('Cada range deve ter percentual maior que zero.');
            }
            if (faixaMin < 0) {
                throw new Error('Valor mínimo não pode ser negativo.');
            }
            if (faixaMax !== null && faixaMax < faixaMin) {
                throw new Error('Valor máximo não pode ser menor que o valor mínimo.');
            }
        }

        return regrasPreenchidas.map((regra) => ({
            faixaMinTotal: regra.faixaMinTotal ?? 0,
            faixaMaxTotal: regra.faixaMaxTotal,
            percentual: regra.percentual,
            momento: regra.momento,
        }));
    };

    const salvarFeeRegrasAgencia = async (clienteId) => {
        if (!clienteId) {
            return;
        }

        const regras = validarENormalizarFeeRegrasParaSalvar();

        await api({
            method: 'put',
            action: `/participante/${clienteId}/feeRegrasAgencia`,
            token: token,
            data: { regras }
        });
    };

    const preencherCep = (cep, dadosAtuais) => {
        buscarCep(cep).then((resposta) => {
            if (!resposta.erro) {
                setDados({
                    ...dadosAtuais,
                    ...{
                        enderecobairro: resposta.bairro,
                        enderecocomplemento: resposta.complemento,
                        enderecologradouro: resposta.logradouro,
                        enderecopais: "30",
                        nomeEnderecopais: "Brasil",
                        enderecomunicipio: resposta.ibge,
                        nomeEnderecomunicipio: resposta.localidade
                    }
                });
            }
        });
    };

    const carregarContasAccess = () => {
        api({ method: 'get', action: '/financeiroCaixa', token: token, params: {} })
            .then(response => {
                const lista = (response.data.data || []).map(caixa => ({
                    value: String(caixa.id),
                    // Agência/conta no rótulo para o operador conferir que escolheu a
                    // conta certa sem sair da tela — é o que sai impresso na ND.
                    label: caixa.agencia || caixa.conta
                        ? caixa.nome + ' — ag. ' + (caixa.agencia || '?') + ' / cc. ' + (caixa.conta || '?')
                        : caixa.nome + ' (sem agência/conta cadastrada)',
                }));
                setContasAccess(lista);
            })
            .catch(() => setContasAccess([]));
    };

    // Rótulo da conta já gravada. Enquanto a lista não chega (ou se a conta foi
    // excluída do cadastro de caixas), mostra o id em vez de campo vazio — campo
    // vazio faria o operador achar que não há nada configurado e salvar por cima.
    const contaRecebimentoSelecionada = () => {
        if (!dados.conta_recebimento) {
            return null;
        }
        const idConta = String(dados.conta_recebimento);
        const achada = contasAccess.find(opcao => opcao.value === idConta);
        return achada || { value: idConta, label: 'Conta ' + idConta + ' (não encontrada)' };
    };

    const limparCobranca = () => {
        setDados({
            ...dados,
            ...{ conta_recebimento: null, prazopagamento: null, dia_corte: null }
        });
    };

    const temCobranca = !!dados.conta_recebimento
        || (dados.prazopagamento !== null && dados.prazopagamento !== undefined && dados.prazopagamento !== '')
        || !!dados.dia_corte;

    const buscar = async () => {
        setIsLoading(true);
        if (id) {
            try {
                const response = await api({
                    method: "get",
                    action: '/participante/' + id,
                    token: token,
                });
                //extrai dados internos para o array principal
                var dadosRecebidos = extract({
                    jsonLaravel: response.data.data,
                    chaves: [
                        'enderecopais',
                        'aereomunicipioembarque',
                        'enderecomunicipio',
                        'banco',
                    ]
                });

                delete dadosRecebidos.tipoparticipante;

                setDados(dadosRecebidos);
                await carregarFeeRegrasAgencia(id);
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        } else {
            setDados({ ...dados, ...{ tipoCadastro: 'cliente' } });
            setFeeRegrasAgencia([]);
            setIsLoading(false);
        }
    };

    // Campo de cobrança vazio tem de chegar ao backend como null, não como "".
    // `conta_recebimento` é bigint no banco e `prazopagamento` é int: string vazia
    // viraria erro ou 0. O backend repete a normalização (é a camada que vale),
    // mas mandar limpo daqui evita depender dela para o caso trivial.
    const comCobrancaNormalizada = (payload) => {
        const vazio = (valor) => valor === '' || valor === undefined || valor === null;
        return {
            ...payload,
            ...{
                conta_recebimento: vazio(payload.conta_recebimento) ? null : payload.conta_recebimento,
                prazopagamento: vazio(payload.prazopagamento) ? null : payload.prazopagamento,
                dia_corte: vazio(payload.dia_corte) ? null : payload.dia_corte,
            }
        };
    };

    const salvar = async () => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            if (id) {//update
                await api({
                    method: 'put',
                    action: '/participante/' + id,
                    token: token,
                    data: comCobrancaNormalizada(dados),
                });

                await salvarFeeRegrasAgencia(id);
                toast.success('Cliente salvo com sucesso!');
                history.push("/cliente");
            } else {//insert
                const response = await api({
                    method: 'post',
                    action: '/participante',
                    token: token,
                    data: comCobrancaNormalizada(dados)
                });

                const clienteId = response?.data?.data?.id;
                if (clienteId) {
                    await salvarFeeRegrasAgencia(clienteId);
                }

                toast.success('Cliente salvo com sucesso!');
                history.push("/cliente");
            }
        } catch (error) {
            toast.error('Erro ao salvar: ' + (error.response?.data?.error ?? error.message ?? ''));
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        document.querySelector("#nome") && document.querySelector("#nome").focus();
        carregarContasAccess();
        buscar();
    },
        // eslint-disable-next-line
        []);

    return (
        <ErpTemplate processando={isLoading}>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div><h1 className="fin-pp__title">Cliente</h1></div>
                </div>

                <form onSubmit={e => { salvar(); e.preventDefault(); }}>
                    {/* Tabs nav */}
                    <div className="fin-pp__tabs mb-3">
                        {[
                            { key: 'geral', label: 'Geral' },
                            { key: 'endereco', label: 'Endereço' },
                            { key: 'fiscal', label: 'Fiscal' },
                            { key: 'banco', label: 'Banco / Cobrança' },
                            { key: 'feeagencia', label: 'Fee da Agência' },
                            { key: 'logomarca', label: 'Logomarca' },
                        ].map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={'fin-pp__tab' + (tab === item.key ? ' is-active' : '')}
                                onClick={() => setTab(item.key)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab: Geral */}
                    {tab === 'geral' && (
                        <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                                <h2 className="fin-pp__cphead-title">Dados Gerais</h2>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="Situação">
                                    <ReactSelect
                                        value={{ value: dados.situacaoRegistro, label: dados.nomeSituacaoRegistro }}
                                        onChange={e => setDados({ ...dados, ...{ "situacaoRegistro": e.value, "nomeSituacaoRegistro": e.label } })}
                                        options={SITUACAO_OPTS}
                                    />
                                </FinField>
                                <FinField label="Código">
                                    <FinInput
                                        id="codigo"
                                        value={dados.codigo || ''}
                                        onChange={e => setDados({ ...dados, ...{ "codigo": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Tipo Cadastro">
                                    <ReactSelect
                                        value={{ value: dados.tipoCadastro, label: dados.nomeTipoCadastro }}
                                        onChange={e => setDados({ ...dados, ...{ "tipoCadastro": e.value, "nomeTipoCadastro": e.label } })}
                                        options={TIPO_CADASTRO_OPTS}
                                    />
                                </FinField>
                                <FinField label="Modelo">
                                    <ReactSelect
                                        value={{ value: dados.modelo, label: dados.nomeModelo }}
                                        onChange={e => setDados({ ...dados, ...{ "modelo": e.value, "nomeModelo": e.label } })}
                                        options={MODELO_OPTS}
                                    />
                                </FinField>
                                <FinField label="Categoria">
                                    <ReactSelect
                                        id="categoriacliente"
                                        value={CATEGORIA_CLIENTE_OPTS.find(opt => opt.value === (dados.categoriacliente || CATEGORIA_FARMACEUTICO))}
                                        onChange={e => setDados({ ...dados, ...{ "categoriacliente": e.value } })}
                                        options={CATEGORIA_CLIENTE_OPTS}
                                    />
                                </FinField>
                                <FinField label="Nome" grow>
                                    <FinInput
                                        id="nome"
                                        value={dados.nome || ''}
                                        onChange={e => setDados({ ...dados, ...{ "nome": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Contato">
                                    <FinInput
                                        id="contato"
                                        value={dados.contato || ''}
                                        onChange={e => setDados({ ...dados, ...{ "contato": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Telefone">
                                    <FinInput
                                        id="telefone"
                                        value={dados.telefone || ''}
                                        onChange={e => setDados({ ...dados, ...{ "telefone": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Celular">
                                    <InputMask
                                        mask="(99) 99999-9999"
                                        id="celular"
                                        value={dados.celular || ''}
                                        onChange={e => setDados({ ...dados, ...{ "celular": e.target.value } })}
                                    >
                                        {(inputProps) => <input {...inputProps} className="fin-pp__input" style={{ paddingLeft: 14 }} />}
                                    </InputMask>
                                </FinField>
                                <FinField label="E-mail" full>
                                    <FinInput
                                        id="email"
                                        value={dados.email || ''}
                                        onChange={e => setDados({ ...dados, ...{ "email": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Observações" full>
                                    <FinTextarea
                                        id="observacao"
                                        value={dados.observacao || ''}
                                        onChange={e => setDados({ ...dados, ...{ "observacao": e.target.value } })}
                                        style={{ minHeight: '100px' }}
                                    />
                                </FinField>
                            </div>
                        </div>
                    )}

                    {/* Tab: Endereço */}
                    {tab === 'endereco' && (
                        <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                                <h2 className="fin-pp__cphead-title">Endereço</h2>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="Rua / Logradouro" grow>
                                    <FinInput
                                        id="enderecologradouro"
                                        value={dados.enderecologradouro || ''}
                                        onChange={e => setDados({ ...dados, ...{ "enderecologradouro": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Número">
                                    <FinInput
                                        id="endereconumero"
                                        value={dados.endereconumero || ''}
                                        onChange={e => setDados({ ...dados, ...{ "endereconumero": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Complemento" grow>
                                    <FinInput
                                        id="enderecocomplemento"
                                        value={dados.enderecocomplemento || ''}
                                        onChange={e => setDados({ ...dados, ...{ "enderecocomplemento": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="CEP">
                                    <FinInput
                                        id="enderecocep"
                                        value={dados.enderecocep || ''}
                                        onChange={(e) => {
                                            dados.enderecocep = e.target.value;
                                            preencherCep(e.target.value, dados);
                                            setDados({ ...dados });
                                        }}
                                    />
                                </FinField>
                                <FinField label="Bairro">
                                    <FinInput
                                        id="enderecobairro"
                                        value={dados.enderecobairro || ''}
                                        onChange={e => setDados({ ...dados, ...{ "enderecobairro": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Cidade">
                                    <ReactSelect
                                        value={{ value: dados.enderecomunicipio, label: dados.nomeEnderecomunicipio }}
                                        onChange={e => setDados({ ...dados, ...{ "enderecomunicipio": e.value, "nomeEnderecomunicipio": e.label } })}
                                        loadOptions={(input) => {
                                            return api({
                                                method: 'get',
                                                action: '/municipio',
                                                token: token,
                                                params: { nome: input }
                                            })
                                                .then(response => {
                                                    let options = response.data.data.map(registro => ({ value: registro.id, label: registro.nome + "-" + registro.uf }));
                                                    return options;
                                                })
                                                .catch(function (error) {
                                                    //console.log(error);
                                                });
                                        }}
                                    />
                                </FinField>
                                <FinField label="País">
                                    <ReactSelect
                                        value={{ value: dados.enderecopais, label: dados.nomeEnderecopais }}
                                        onChange={e => setDados({ ...dados, ...{ "enderecopais": e.value, "nomeEnderecopais": e.label } })}
                                        loadOptions={(input) => {
                                            return api({
                                                method: 'get',
                                                action: '/pais',
                                                token: token,
                                                params: { nome: input }
                                            })
                                                .then(response => {
                                                    let options = response.data.data.map(registro => ({ value: registro.id, label: registro.nome }));
                                                    return options;
                                                })
                                                .catch(function (error) {
                                                    //console.log(error);
                                                });
                                        }}
                                    />
                                </FinField>
                            </div>
                        </div>
                    )}

                    {/* Tab: Fiscal */}
                    {tab === 'fiscal' && (
                        <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                                <h2 className="fin-pp__cphead-title">Fiscal</h2>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="CNPJ">
                                    <FinInput
                                        id="cnpj"
                                        value={dados.cnpj || ''}
                                        onChange={e => setDados({ ...dados, ...{ "cnpj": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="CPF">
                                    <FinInput
                                        id="cpf"
                                        value={dados.cpf || ''}
                                        onChange={e => setDados({ ...dados, ...{ "cpf": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Inscrição Estadual">
                                    <FinInput
                                        id="inscricaoestadual"
                                        value={dados.inscricaoestadual || ''}
                                        onChange={e => setDados({ ...dados, ...{ "inscricaoestadual": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Indicador de Inscrição">
                                    <ReactSelect
                                        value={{ value: dados.indicadorinscricao, label: dados.nomeIndicadorinscricao }}
                                        onChange={e => setDados({ ...dados, ...{ "indicadorinscricao": e.value, "nomeIndicadorinscricao": e.label } })}
                                        options={INDICADOR_INSCRICAO_OPTS}
                                    />
                                </FinField>
                                <FinField label="Inscrição Municipal">
                                    <FinInput
                                        id="inscricaomunicipal"
                                        value={dados.inscricaomunicipal || ''}
                                        onChange={e => setDados({ ...dados, ...{ "inscricaomunicipal": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Regime Tributário">
                                    <ReactSelect
                                        value={{ value: dados.regimetributario, label: dados.nomeRegimetributario }}
                                        onChange={e => setDados({ ...dados, ...{ "regimetributario": e.value, "nomeRegimetributario": e.label } })}
                                        options={REGIME_TRIBUTARIO_OPTS}
                                    />
                                </FinField>
                                <FinField label="RG">
                                    <FinInput
                                        id="rg"
                                        value={dados.rg || ''}
                                        onChange={e => setDados({ ...dados, ...{ "rg": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Nome Fantasia">
                                    <FinInput
                                        id="nomefantasia"
                                        value={dados.nomefantasia || ''}
                                        onChange={e => setDados({ ...dados, ...{ "nomefantasia": e.target.value } })}
                                    />
                                </FinField>
                            </div>
                        </div>
                    )}

                    {/* Tab: Banco */}
                    {tab === 'banco' && (
                        <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                                <div>
                                    <h2 className="fin-pp__cphead-title">Banco do cliente</h2>
                                    <p className="fin-pp__cphead-sub">
                                        Conta bancária do próprio cliente. Usada quando a Access precisa
                                        pagar ou devolver algo a ele.
                                    </p>
                                </div>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="Banco">
                                    <ReactSelect
                                        value={{ value: dados.banco, label: dados.nomeBanco }}
                                        onChange={e => setDados({ ...dados, ...{ "banco": e.value, "nomeBanco": e.label } })}
                                        loadOptions={(input) => {
                                            return api({
                                                method: 'get',
                                                action: '/financeiroBanco',
                                                token: token,
                                                params: { nome: input }
                                            })
                                                .then(response => {
                                                    let options = response.data.data.map(registro => ({ value: registro.id, label: registro.codigo + " - " + registro.nome }));
                                                    return options;
                                                })
                                                .catch(function (error) {
                                                    //console.log(error);
                                                });
                                        }}
                                    />
                                </FinField>
                                <FinField label="Agência">
                                    <FinInput
                                        id="bancoagencia"
                                        value={dados.bancoagencia || ''}
                                        onChange={e => setDados({ ...dados, ...{ "bancoagencia": e.target.value } })}
                                    />
                                </FinField>
                                <FinField label="Conta">
                                    <FinInput
                                        id="bancoconta"
                                        value={dados.bancoconta || ''}
                                        onChange={e => setDados({ ...dados, ...{ "bancoconta": e.target.value } })}
                                    />
                                </FinField>
                            </div>
                        </div>
                    )}

                    {/* Cobrança — mesma aba do "Banco do cliente" de propósito: os dois blocos
                        falam de conta bancária e são fáceis de confundir. Lado a lado, com o
                        contraste escrito, o operador vê que este aqui é o caminho INVERSO
                        (dinheiro entrando na Access) antes de digitar o banco errado. */}
                    {tab === 'banco' && (
                        <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ marginBottom: 0 }}>
                                <span className="fin-pp__cphead-ic"><FaFileInvoiceDollar size={14} /></span>
                                <div>
                                    <h2 className="fin-pp__cphead-title">Cobrança</h2>
                                    <p className="fin-pp__cphead-sub">
                                        O que a Nota de Débito imprime quando a Access cobra deste cliente:
                                        rodapé bancário e data de vencimento.
                                    </p>
                                </div>
                            </div>

                            <div className="fin-pp__notice" style={{ margin: '0 0 14px' }}>
                                <span className="fin-pp__pill is-info" style={{ flex: 'none' }}>Atenção</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    A conta abaixo é <strong>da Access</strong> — é onde este cliente
                                    deposita. Não é a conta bancária dele (essa é o bloco de cima).
                                </span>
                            </div>

                            {/* flex-start em vez do flex-end padrão: o "Dia de corte" carrega um
                                texto de ajuda dentro do campo, e alinhado pela base o input dele
                                subiria em relação aos outros dois. Alinhando pelo topo, os três
                                rótulos e os três inputs ficam na mesma linha. */}
                            <div className="fin-pp__filters" style={{ alignItems: 'flex-start' }}>
                                <FinField
                                    label="Conta da Access que recebe deste cliente"
                                    ajuda="Sai impressa no rodapé da ND (banco, agência e conta). As opções são as contas cadastradas em Financeiro › Caixa; agência e conta de cada uma são preenchidas lá."
                                    grow
                                >
                                    <ReactSelect
                                        isClearable
                                        placeholder="Nenhuma conta definida"
                                        options={contasAccess}
                                        value={contaRecebimentoSelecionada()}
                                        onChange={e => setDados({ ...dados, ...{ "conta_recebimento": e ? e.value : null } })}
                                    />
                                </FinField>
                                <FinField
                                    label="Prazo de pagamento (dias)"
                                    ajuda="Vencimento da ND = data de emissão + este prazo. Em branco, a ND sai sem data de vencimento sugerida."
                                >
                                    <FinInput
                                        id="prazopagamento"
                                        type="number"
                                        min={0}
                                        value={dados.prazopagamento ?? ''}
                                        onChange={e => setDados({ ...dados, ...{ "prazopagamento": e.target.value } })}
                                        style={{ textAlign: 'right' }}
                                    />
                                </FinField>
                                <FinField
                                    label="Prazo para faturar (dias)"
                                    ajuda="Conta do FIM DO EVENTO até a data limite de faturamento. Ex.: 30 = evento terminou 04/09, fatura até 04/10. Em branco, não há alerta."
                                >
                                    <FinInput
                                        id="prazo_faturamento"
                                        type="number"
                                        min={0}
                                        value={dados.prazo_faturamento ?? ''}
                                        onChange={e => setDados({ ...dados, ...{ "prazo_faturamento": e.target.value } })}
                                        style={{ textAlign: 'right' }}
                                    />
                                </FinField>
                                <FinField label="Dia de corte" grow>
                                    <FinInput
                                        id="dia_corte"
                                        maxLength={40}
                                        placeholder="às quintas, 10/20/30, sextas…"
                                        value={dados.dia_corte || ''}
                                        onChange={e => setDados({ ...dados, ...{ "dia_corte": e.target.value } })}
                                    />
                                    <span style={{ fontSize: 11, color: 'var(--fin-mt)', marginTop: 4, display: 'block' }}>
                                        Texto livre, só informativo: diz quando o cliente costuma pagar.
                                        Não entra no cálculo — o vencimento sai do prazo acima.
                                    </span>
                                </FinField>
                            </div>

                            {/* "Remover" aqui é limpar os três campos, não excluir o cliente.
                                Sem este botão explícito o operador procura um "excluir" que não
                                existe — ou apaga o cliente inteiro para tirar a cobrança. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                    disabled={!temCobranca}
                                    onClick={limparCobranca}
                                >
                                    <FaEraser size={13} /> Limpar dados de cobrança
                                </button>
                                <span style={{ fontSize: 11.5, color: 'var(--fin-mt)' }}>
                                    {temCobranca
                                        ? 'Zera os três campos acima. O cliente continua cadastrado — confirme em Salvar.'
                                        : 'Nada configurado. A ND deste cliente sai sem rodapé bancário e sem vencimento.'}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Tab: Fee da Agência */}
                    {tab === 'feeagencia' && (
                        <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                                <h2 className="fin-pp__cphead-title">Fee da Agência</h2>
                            </div>
                            <p style={{ marginBottom: 12, color: 'var(--fin-mt)', fontSize: 14 }}>
                                Configure as faixas de Fee da Agência para cálculo automático no orçamento.
                            </p>

                            <div className="fin-pp__tblwrap" style={{ marginBottom: 12 }}>
                                <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                    <thead>
                                        <tr>
                                            <th>Valor mínimo (R$)</th>
                                            <th>Valor máximo (R$)</th>
                                            <th>Percentual (%)</th>
                                            <th style={{ width: 60 }}>Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(feeRegrasAgencia.length > 0 ? feeRegrasAgencia : [regraVazia()]).map((regra, index) => (
                                            <tr key={`regra-fee-${index}`}>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="fin-pp__input"
                                                        style={{ paddingLeft: 14 }}
                                                        placeholder="0,00"
                                                        value={regra.faixaMinTotal}
                                                        onChange={(e) => {
                                                            const lista = [...(feeRegrasAgencia.length > 0 ? feeRegrasAgencia : [regraVazia()])];
                                                            lista[index] = { ...lista[index], faixaMinTotal: e.target.value };
                                                            setFeeRegrasAgencia(lista);
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="fin-pp__input"
                                                        style={{ paddingLeft: 14 }}
                                                        placeholder="em branco = sem teto"
                                                        value={regra.faixaMaxTotal}
                                                        onChange={(e) => {
                                                            const lista = [...(feeRegrasAgencia.length > 0 ? feeRegrasAgencia : [regraVazia()])];
                                                            lista[index] = { ...lista[index], faixaMaxTotal: e.target.value };
                                                            setFeeRegrasAgencia(lista);
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="fin-pp__input"
                                                        style={{ paddingLeft: 14 }}
                                                        placeholder="0,0000"
                                                        value={regra.percentual}
                                                        onChange={(e) => {
                                                            const lista = [...(feeRegrasAgencia.length > 0 ? feeRegrasAgencia : [regraVazia()])];
                                                            lista[index] = { ...lista[index], percentual: e.target.value };
                                                            setFeeRegrasAgencia(lista);
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm"
                                                        title="Remover range"
                                                        onClick={() => {
                                                            const lista = [...(feeRegrasAgencia.length > 0 ? feeRegrasAgencia : [regraVazia()])];
                                                            lista.splice(index, 1);
                                                            setFeeRegrasAgencia(lista);
                                                        }}
                                                    >
                                                        <FaTrash size={12} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div>
                                <button
                                    type="button"
                                    className="fin-pp__btn"
                                    onClick={() => {
                                        setFeeRegrasAgencia((atual) => [...(atual || []), regraVazia()]);
                                    }}
                                >
                                    <FaPlus size={12} /> Adicionar range
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Tab: Logomarca */}
                    {tab === 'logomarca' && (
                        <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                            <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                                <h2 className="fin-pp__cphead-title">Logomarca</h2>
                            </div>
                            <div className="fin-pp__filters">
                                <FinField label="Logomarca (.jpeg)">
                                    <input
                                        type="file"
                                        multiple={false}
                                        className="fin-pp__input"
                                        style={{ paddingLeft: 14 }}
                                        id="logomarca"
                                        onChange={(e) => {
                                            const file = e.target.files && e.target.files[0];
                                            if (!file) return;
                                            const nomeLogomarca = e.target.value;
                                            //transforma binario em json hexadecimal
                                            const fileReader = new FileReader();
                                            // onload ANTES do read; setDados funcional pra não perder o binário
                                            // (evita closure stale / update em componente possivelmente remontado).
                                            fileReader.onload = (ev) => {
                                                setDados(prev => ({
                                                    ...prev,
                                                    "nomeLogomarca": nomeLogomarca,
                                                    "binarioLogomarca": bin2hex(ev.target.result),
                                                }));
                                            };
                                            fileReader.readAsBinaryString(file);
                                        }}
                                    />
                                </FinField>
                            </div>
                            {dados.logomarca?.link ? (
                                <div style={{ marginTop: 12 }}>
                                    <img alt="" src={dados.logomarca.link} width={"400px"} />
                                </div>
                            ) : null}
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                        <button type="submit" className="fin-pp__btn fin-pp__btn--primary" disabled={isSubmitting}>
                            {isSubmitting
                                ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
                                : <><FaSave size={16} /> Salvar</>
                            }
                        </button>
                    </div>
                </form>
            </div>
        </ErpTemplate>
    );
}
